import { useState } from 'react';

import { DragDropProvider } from '@dnd-kit/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { LostDeal, PostponeDeal, useInWorkOrFinishDealMutation } from '@crm/features';
import { Button, Separator } from '@crm/shared/ui';
import { Spinner } from '@shared/ui';

import { bucketRoutesByKey, bucketStylesByKey } from './config';
import { getTerminalBucketDropResult } from './lib';
import { useDealsBoardDnd, useDealsBoardDragEnd } from './model';
import {
    BoardBox,
    DealDragOverlay,
    DealsPageFooter,
    DealsPageHeader,
    DealsPageMain,
    BucketContext,
} from './ui';

import type { TDeal } from '@crm/entities/deal';
import type { DragEndEvent } from '@dnd-kit/react';

export const DealsPage = () => {
    const { t } = useTranslation();
    const [lostDealId, setLostDealId] = useState<TDeal['id'] | null>(null);
    const [postponedDealId, setPostponedDealId] = useState<TDeal['id'] | null>(null);
    const markDealsInWorkOrFinishedMutation = useInWorkOrFinishDealMutation({
        invalidationScope: 'board',
    });
    const {
        activeDealId,
        columns,
        completeDealDragCleanup,
        dealsBoardQuery,
        getColumnDragEndResult,
        getDealDragEndResult,
        handleDragEnd,
        handleDragOver,
        handleDragStart,
        isTerminalBucketOver,
    } = useDealsBoardDnd();
    const { handleDndDragEnd } = useDealsBoardDragEnd({
        handleDragEnd,
        getColumnDragEndResult,
        getDealDragEndResult,
    });
    const { isError, isFetching, isPending, refetch } = dealsBoardQuery;
    const handleDealsDragEnd = (event: DragEndEvent) => {
        const terminalBucketDropResult = getTerminalBucketDropResult(event);

        if (terminalBucketDropResult?.bucketKey === 'lost') {
            setLostDealId(terminalBucketDropResult.dealId);
            return;
        }

        if (terminalBucketDropResult?.bucketKey === 'postponed') {
            setPostponedDealId(terminalBucketDropResult.dealId);
            return;
        }

        if (terminalBucketDropResult?.bucketKey === 'in_work_or_finished') {
            markDealsInWorkOrFinishedMutation.mutate({
                dealIds: [terminalBucketDropResult.dealId],
            });
            return;
        }

        handleDndDragEnd(event);
    };

    if (isPending) {
        return <Spinner />;
    }

    if (isError) {
        return (
            <div className="flex flex-col items-start gap-3">
                <div>{t('crm.deals.page.loadError')}</div>
                <Button disabled={isFetching} onClick={() => refetch()} type="button">
                    {isFetching ? t('loading.text') : t('button.retry')}
                </Button>
            </div>
        );
    }

    return (
        <section className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
            <DealsPageHeader />

            <DragDropProvider
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDealsDragEnd}
            >
                <DealsPageMain
                    activeDealId={activeDealId}
                    columnColors={dealsBoardQuery.data.contextData.columnColors}
                    columns={columns}
                    hideColumnDropPlaceholder={isTerminalBucketOver}
                />

                <DealDragOverlay columns={columns} onDragCleanup={completeDealDragCleanup} />

                <Separator className="border-t-2 border-t-smtr-border-100" />

                <DealsPageFooter>
                    {dealsBoardQuery?.data?.terminalBuckets.map((bucket) => (
                        <Link
                            key={bucket.key}
                            to={bucketRoutesByKey[bucket.key]}
                            className="flex-1"
                        >
                            <BoardBox
                                bucketData={bucket}
                                boardHeaderTextClassName={bucketStylesByKey[bucket.key]}
                                aside={<BucketContext contextData={bucket.context} />}
                            />
                        </Link>
                    ))}
                </DealsPageFooter>
            </DragDropProvider>

            {lostDealId !== null && (
                <LostDeal
                    dealIds={[lostDealId]}
                    invalidationScope="board"
                    isOpen
                    trigger={null}
                    onOpenChange={(open) => {
                        if (!open) {
                            setLostDealId(null);
                        }
                    }}
                />
            )}

            {postponedDealId !== null && (
                <PostponeDeal
                    dealIds={[postponedDealId]}
                    isOpen
                    trigger={null}
                    onOpenChange={(open) => {
                        if (!open) {
                            setPostponedDealId(null);
                        }
                    }}
                />
            )}
        </section>
    );
};
